/**
 * AnimationSequence Behavior Editor
 * UI for editing AnimationSequence behavior with enter/main/exit state editors
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { AnimationSequenceData, AnimationStateConfig } from 'trace-viewer/animation';
import { AnimationSequence } from 'trace-viewer/behaviors';
import { isPropertyBinding } from 'trace-viewer/behaviors';
import { bindAnimationClipToProperty, applyClipSrc } from 'trace-viewer/editor';
import type { ResourceManager } from 'trace-viewer/scene';
import { EntityType } from 'trace-viewer/cms';
import { AssetField } from '../components/asset-field';
import { CatalogBrowseButton } from '../components/catalog-asset-picker';

// ============================================================================
// Shared Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '80px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px',
    } as React.CSSProperties,
    stateSection: {
        backgroundColor: '#1e1e1e',
        borderRadius: '4px',
        padding: '10px',
        marginBottom: '8px',
    } as React.CSSProperties,
    stateHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
        paddingBottom: '6px',
        borderBottom: '1px solid #333',
    } as React.CSSProperties,
    stateTitle: {
        fontSize: '12px',
        fontWeight: 600,
        color: '#ccc',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
    } as React.CSSProperties,
};

// ============================================================================
// State Editor Component
// ============================================================================

interface StateEditorProps {
    stateName: 'enter' | 'main' | 'exit';
    stateConfig: AnimationStateConfig | undefined;
    onChange: (config: AnimationStateConfig | undefined) => void;
    onPreview: () => void;
    onStop: () => void;
    isPlaying: boolean;
    currentState: string;
    resources?: ResourceManager;
    behavior: AnimationSequence;
    /** Called after a binding-level mutation (bind / bound-src pick) so the parent re-reads data. */
    onBindingChanged: () => void;
}

const StateEditor: React.FC<StateEditorProps> = ({
    stateName,
    stateConfig,
    onChange,
    onPreview,
    onStop,
    isPlaying: _isPlaying,
    currentState,
    resources,
    behavior,
    onBindingChanged,
}) => {
    const config = stateConfig ?? {};
    const isActive = currentState === stateName;
    const [clipNames, setClipNames] = useState<string[]>([]);

    // A slot is "state-swappable" when its authored src is a property binding
    // (`$animMain`). The resolved src (what states drive) comes from the behavior.
    const isBound = typeof config.src === 'string' && isPropertyBinding(config.src);
    const resolvedSrc = behavior.getData().states?.[stateName]?.src;
    const effectiveSrc = isBound ? behavior[
        stateName === 'enter' ? 'getEnter' : stateName === 'main' ? 'getMain' : 'getExit'
    ]().src : resolvedSrc;

    // Fetch clip names for whatever src actually resolves (state-driven when bound).
    useEffect(() => {
        let cancelled = false;
        const src = effectiveSrc;

        if (src) {
            void behavior.getClipNamesForSrc(src).then(names => {
                if (!cancelled) setClipNames(names);
            });
        } else {
            setClipNames([]);
        }

        return () => { cancelled = true; };
    }, [effectiveSrc, behavior]);

    const handleChange = (field: keyof AnimationStateConfig, value: string | number | undefined) => {
        const newConfig = { ...config, [field]: value };
        // Clean up undefined/empty values
        if (value === '' || value === undefined) {
            delete newConfig[field];
        }
        // If config is now empty, pass undefined
        if (Object.keys(newConfig).length === 0) {
            onChange(undefined);
        } else {
            onChange(newConfig);
        }
    };

    // Turn this slot into a state-swappable binding (declare a property + bind src).
    const handleBind = useCallback(() => {
        void bindAnimationClipToProperty(behavior.sceneObject, { slot: stateName })
            .then(onBindingChanged);
    }, [behavior, stateName, onBindingChanged]);

    // Edit a bound slot's clip: routes into the active state (or declaration base)
    // without clobbering the `$ref` binding.
    const handleBoundSrc = useCallback((src: string) => {
        void applyClipSrc(behavior.sceneObject, stateName, src).then(onBindingChanged);
    }, [behavior, stateName, onBindingChanged]);

    // Unified src setter for both the file field and the catalog picker: bound
    // slots route through applyClipSrc; unbound slots write the config directly.
    const setSrc = useCallback((src: string) => {
        if (isBound) handleBoundSrc(src);
        else handleChange('src', src);
    }, [isBound, handleBoundSrc]); // eslint-disable-line react-hooks/exhaustive-deps

    // Bound clip: setting `clip` writes the AnimationSequence config directly
    // (the clip name is not state-bound in v1, only src).
    const handleBoundClip = useCallback((clip: string | undefined) => {
        handleChange('clip', clip);
    }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

    const getStateLabel = () => {
        switch (stateName) {
            case 'enter': return 'Enter';
            case 'main': return 'Main (Loop)';
            case 'exit': return 'Exit';
        }
    };

    const getStateDescription = () => {
        switch (stateName) {
            case 'enter': return 'Plays once when object enters';
            case 'main': return 'Loops while object is active';
            case 'exit': return 'Plays once when object exits';
        }
    };

    return (
        <div style={{
            ...styles.stateSection,
            borderLeft: isActive ? '3px solid #4caf50' : '3px solid transparent',
        }}>
            <div style={styles.stateHeader}>
                <div>
                    <div style={styles.stateTitle}>{getStateLabel()}</div>
                    <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                        {getStateDescription()}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                    {isBound ? (
                        <span
                            title="This clip is state-swappable — pick clips per state in the Properties › States panel"
                            style={{ fontSize: '10px', color: '#4caf50', alignSelf: 'center' }}
                        >
                            🔗 state-swappable
                        </span>
                    ) : (
                        <button
                            onClick={handleBind}
                            title="Bind this clip to a property so states can swap it"
                            style={{
                                padding: '3px 8px',
                                backgroundColor: '#37474f',
                                border: '1px solid #546e7a',
                                borderRadius: '3px',
                                color: '#b0bec5',
                                cursor: 'pointer',
                                fontSize: '10px',
                            }}
                        >
                            Make state-swappable
                        </button>
                    )}
                    <button
                        onClick={onPreview}
                        disabled={!effectiveSrc}
                        style={{
                            padding: '3px 8px',
                            backgroundColor: isActive ? '#ff9800' : '#4caf50',
                            border: 'none',
                            borderRadius: '3px',
                            color: '#fff',
                            cursor: effectiveSrc ? 'pointer' : 'not-allowed',
                            fontSize: '10px',
                            opacity: effectiveSrc ? 1 : 0.5,
                        }}
                    >
                        {isActive ? 'Playing' : 'Preview'}
                    </button>
                    {isActive && (
                        <button
                            onClick={onStop}
                            style={{
                                padding: '3px 8px',
                                backgroundColor: '#555',
                                border: 'none',
                                borderRadius: '3px',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '10px',
                            }}
                        >
                            Stop
                        </button>
                    )}
                </div>
            </div>

            {/* Source GLB — when bound, edits route through applyClipSrc (active
                state override / declaration base) so the binding is preserved.
                Local drop (AssetField) + catalog browse are both offered. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <AssetField
                        label="Source"
                        value={effectiveSrc ?? ''}
                        onChange={setSrc}
                        assetType="model"
                        resources={resources}
                        labelWidth="80px"
                    />
                </div>
                <CatalogBrowseButton
                    entityType={EntityType.Animation}
                    onSelect={(asset) => setSrc(asset.url)}
                    title="Browse catalog animations"
                />
            </div>
            {isBound && (
                <div style={{ fontSize: '10px', color: '#666', margin: '-2px 0 6px 88px' }}>
                    Driven by the active state (or Base). Switch states in Properties › States.
                </div>
            )}

            {/* Clip Name */}
            {clipNames.length > 0 && (
                <div style={styles.fieldRow}>
                    <span style={styles.label}>Clip</span>
                    <select
                        value={config.clip ?? ''}
                        onChange={(e) => (isBound ? handleBoundClip(e.target.value || undefined) : handleChange('clip', e.target.value || undefined))}
                        style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                    >
                        <option value="">default ({clipNames[0]})</option>
                        {clipNames.map((name) => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Speed */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Speed</span>
                <input
                    type="number"
                    value={config.speed ?? 1}
                    onChange={(e) => handleChange('speed', parseFloat(e.target.value) || 1)}
                    step={0.1}
                    min={0.1}
                    style={{ ...styles.input, width: '70px' }}
                />
            </div>

            {/* Delay — enter: posed hold before the entrance plays (staggered
                builds); exit: departure offset in sequenced page changes.
                The main loop doesn't read it, so the field is hidden there.
                0 = the default → stored as absent. */}
            {stateName !== 'main' && (
                <div style={styles.fieldRow}>
                    <span style={styles.label}>Delay (s)</span>
                    <input
                        type="number"
                        value={config.delay ?? 0}
                        onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            handleChange('delay', Number.isFinite(v) && v > 0 ? v : undefined);
                        }}
                        step={0.1}
                        min={0}
                        style={{ ...styles.input, width: '70px' }}
                    />
                </div>
            )}
        </div>
    );
};

// ============================================================================
// AnimationSequence Editor Component
// ============================================================================

export interface AnimationSequenceEditorProps {
    data: AnimationSequenceData;
    behavior: AnimationSequence;
    onChange: (data: AnimationSequenceData) => void;
}

export const AnimationSequenceEditor: React.FC<AnimationSequenceEditorProps> = ({
    data,
    behavior,
    onChange,
}) => {
    const [currentState, setCurrentState] = useState<string>('idle');

    // Poll for current playback state
    useEffect(() => {
        const updateState = () => {
            setCurrentState(behavior.getCurrentState());
        };

        updateState();
        const intervalId = setInterval(updateState, 200);
        return () => clearInterval(intervalId);
    }, [behavior]);

    const handleStateChange = useCallback((
        stateName: 'enter' | 'main' | 'exit',
        config: AnimationStateConfig | undefined
    ) => {
        const newStates = { ...data.states };
        if (config) {
            newStates[stateName] = config;
        } else {
            delete newStates[stateName];
        }
        onChange({ ...data, states: newStates });
    }, [data, onChange]);

    const handlePreview = useCallback((stateName: 'enter' | 'main' | 'exit') => {
        behavior.stop();
        switch (stateName) {
            case 'enter': void behavior.getEnter().play(); break;
            case 'main':  void behavior.getMain().play();  break;
            case 'exit':  void behavior.getExit().play();  break;
        }
    }, [behavior]);

    const handleStop = useCallback(() => {
        behavior.stop();
    }, [behavior]);

    const handlePlaySequence = useCallback(() => {
        behavior.stop();
        behavior.playSequence();
    }, [behavior]);

    // After a binding-level mutation (bind / bound-src pick), the util commits
    // straight to the behavior(s) and records its own undo. Re-sync the panel's
    // local data from the (now authoritative) behavior so the UI reflects it.
    const handleBindingChanged = useCallback(() => {
        onChange(behavior.getData());
    }, [behavior, onChange]);

    const hasAnyAnimation = (data.states.enter?.src ?? data.states.main?.src) ?? data.states.exit?.src;

    return (
        <div>
            {/* Global controls */}
            <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: '#1a1a1a',
                borderRadius: '4px',
            }}>
                <button
                    onClick={handlePlaySequence}
                    disabled={!hasAnyAnimation}
                    style={{
                        flex: 1,
                        padding: '6px 12px',
                        backgroundColor: hasAnyAnimation ? '#2196f3' : '#444',
                        border: 'none',
                        borderRadius: '3px',
                        color: '#fff',
                        cursor: hasAnyAnimation ? 'pointer' : 'not-allowed',
                        fontSize: '11px',
                    }}
                >
                    Play Sequence
                </button>
                <button
                    onClick={handleStop}
                    disabled={currentState === 'idle'}
                    style={{
                        padding: '6px 12px',
                        backgroundColor: currentState !== 'idle' ? '#f44336' : '#444',
                        border: 'none',
                        borderRadius: '3px',
                        color: '#fff',
                        cursor: currentState !== 'idle' ? 'pointer' : 'not-allowed',
                        fontSize: '11px',
                    }}
                >
                    Stop All
                </button>
            </div>

            {/* State editors */}
            <StateEditor
                stateName="enter"
                stateConfig={data.states.enter}
                onChange={(config) => handleStateChange('enter', config)}
                onPreview={() => handlePreview('enter')}
                onStop={handleStop}
                isPlaying={currentState !== 'idle'}
                currentState={currentState}
                resources={behavior.sceneObject.resources}
                behavior={behavior}
                onBindingChanged={handleBindingChanged}
            />

            <StateEditor
                stateName="main"
                stateConfig={data.states.main}
                onChange={(config) => handleStateChange('main', config)}
                onPreview={() => handlePreview('main')}
                onStop={handleStop}
                isPlaying={currentState !== 'idle'}
                currentState={currentState}
                resources={behavior.sceneObject.resources}
                behavior={behavior}
                onBindingChanged={handleBindingChanged}
            />

            <StateEditor
                stateName="exit"
                stateConfig={data.states.exit}
                onChange={(config) => handleStateChange('exit', config)}
                onPreview={() => handlePreview('exit')}
                onStop={handleStop}
                isPlaying={currentState !== 'idle'}
                currentState={currentState}
                resources={behavior.sceneObject.resources}
                behavior={behavior}
                onBindingChanged={handleBindingChanged}
            />

            {/* Current state indicator */}
            {currentState !== 'idle' && (
                <div style={{
                    marginTop: '8px',
                    padding: '6px 10px',
                    backgroundColor: '#4caf5020',
                    border: '1px solid #4caf50',
                    borderRadius: '3px',
                    fontSize: '11px',
                    color: '#4caf50',
                    textAlign: 'center',
                }}>
                    Playing: {currentState}
                </div>
            )}
        </div>
    );
};
